import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scale, FileText, Users, Shield, ArrowRight, CheckCircle, Building2, Globe, Briefcase } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const HomePage = () => {
  const openLeadForm = () => {
    window.dispatchEvent(new CustomEvent('openLeadForm'));
  };

  const services = [
    {
      title: 'Corporate & Business Contracts',
      description: 'Comprehensive contract drafting and negotiation for your business needs.',
      icon: FileText,
      path: '/services/corporate-business-contracts'
    },
    {
      title: 'Business Formation',
      description: 'Expert guidance on structuring and forming your business entity.',
      icon: Building2,
      path: '/services/corporate-business-formation'
    },
    {
      title: 'Mergers & Acquisitions',
      description: 'Strategic legal support for complex M&A transactions.',
      icon: Briefcase,
      path: '/services/mergers-acquisitions'
    },
    {
      title: 'Immigration',
      description: 'Navigating immigration law with expertise and care.',
      icon: Globe,
      path: '/services/immigration'
    },
    {
      title: 'Trademark Registration',
      description: 'Protect your brand with comprehensive trademark services.',
      icon: Shield,
      path: '/services/trademark-registration'
    },
    {
      title: 'Business Disputes',
      description: 'Resolving commercial disputes efficiently and effectively.',
      icon: Scale,
      path: '/services/business-disputes'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'Share your legal needs with our expert attorneys through a confidential consultation.'
    },
    {
      step: '02',
      title: 'Strategic Planning',
      description: 'We analyze your case and develop a tailored legal strategy to achieve your goals.'
    },
    {
      step: '03',
      title: 'Expert Execution',
      description: 'Our team implements the strategy with precision, keeping you informed every step of the way.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'CEO, TechStart Inc.',
      content: 'Tiller Kingsley Bale Associates handled our merger with exceptional professionalism. Their expertise in corporate law is unmatched.'
    },
    {
      name: 'Michael Chen',
      role: 'Founder, Global Ventures',
      content: 'The team provided invaluable guidance during our international expansion. Highly recommend their services.'
    },
    {
      name: 'Emily Roberts',
      role: 'Director, Creative Studios',
      content: 'Outstanding trademark and IP protection services. They made the complex process simple and stress-free.'
    }
  ];

  const faqs = [
    {
      question: 'What areas of law does your firm specialize in?',
      answer: 'We specialize in corporate and business law, intellectual property, immigration, mergers and acquisitions, and regulatory compliance. Our experienced attorneys provide comprehensive legal services for both businesses and individuals.'
    },
    {
      question: 'How do I schedule a consultation?',
      answer: 'You can schedule a consultation by clicking the "Get Legal Help" button on our website and filling out the form. Our team will contact you within 24 hours to arrange a meeting at your convenience.'
    },
    {
      question: 'What should I bring to my first consultation?',
      answer: 'Please bring any relevant documents related to your legal matter, including contracts, correspondence, and any previous legal filings. A list of questions or concerns is also helpful.'
    },
    {
      question: 'Do you offer services for startups and small businesses?',
      answer: 'Yes, we work extensively with startups and small businesses, providing services from business formation and contract drafting to intellectual property protection and compliance matters.'
    },
    {
      question: 'What are your consultation fees?',
      answer: 'We offer an initial consultation to understand your needs. Fee structures vary depending on the type and complexity of the legal matter. We provide transparent pricing and discuss fees during your consultation.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold text-slate-900 leading-[1.1] mb-6 heading-shadow">
                Defining the Future of Law
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8">
                Trusted legal counsel for businesses and individuals. We combine deep expertise 
                with unwavering commitment to deliver exceptional legal solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openLeadForm}
                  className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-4 font-serif text-lg transition-all hover:tracking-wider shadow-lg hover:shadow-xl btn-primary"
                  data-testid="hero-cta-button"
                >
                  Get Legal Help
                </button>
                <Link
                  to="/services/corporate-business-contracts"
                  className="border border-slate-200 bg-transparent text-slate-900 hover:bg-slate-50 px-8 py-4 font-serif text-lg transition-colors inline-flex items-center justify-center"
                  data-testid="hero-secondary-button"
                >
                  Explore Services <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1770943558988-2c605d6bd5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBsYXd5ZXIlMjBtZWV0aW5nJTIwZ2xhc3MlMjBvZmZpY2V8ZW58MHx8fHwxNzczNDM4OTEyfDA&ixlib=rb-4.1.0&q=85"
                alt="Professional legal consultation"
                className="w-full h-[400px] md:h-[500px] object-cover shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-y border-slate-100 py-8" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">Trusted</p>
                <p className="text-sm text-slate-600">Legal Partner</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">Expert</p>
                <p className="text-sm text-slate-600">Legal Team</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">Proven</p>
                <p className="text-sm text-slate-600">Track Record</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4"
            >
              Legal Expertise Refined
            </motion.h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive legal services tailored to your unique needs
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex-shrink-0 w-[85vw] md:w-auto snap-center"
                >
                  <Link
                    to={service.path}
                    className="group block bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 p-8 h-full service-card"
                    data-testid={`service-card-${idx}`}
                  >
                    <Icon className="h-12 w-12 text-amber-700 mb-4" />
                    <h3 className="text-2xl font-medium text-slate-900 mb-3 group-hover:text-amber-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <span className="inline-flex items-center text-slate-900 font-medium group-hover:text-amber-700 transition-colors">
                      Learn More <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={openLeadForm}
              className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-4 font-serif text-lg transition-all hover:tracking-wider shadow-lg hover:shadow-xl btn-primary"
              data-testid="services-cta-button"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              How We Work
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              A streamlined process designed for your success
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {howItWorks.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="bg-slate-50 p-8 border-l-4 border-amber-700 hover:shadow-lg transition-shadow flex-shrink-0 w-[85vw] md:w-auto snap-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 text-white text-xl font-bold mb-6">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-slate-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1744605746099-b50b4a92ee54?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="About our firm"
                className="w-full h-[500px] object-cover shadow-xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                Legal Excellence You Can Trust
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Tiller Kingsley Bale Associates has been at the forefront of corporate and business law. 
                Our firm was founded on the principle that exceptional legal service requires both deep expertise and 
                genuine dedication to client success.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                We've grown into one of the region's most respected law firms, representing 
                major corporations, innovative startups, and individuals navigating complex legal challenges.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-amber-700 hover:text-amber-800 font-medium text-lg"
              >
                Learn More About Us <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practice Areas Highlight */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="practice-areas-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              Comprehensive Legal Services
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              Full-service representation across multiple practice areas
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-slate-100 p-8 shadow-sm flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <Building2 className="h-12 w-12 text-amber-700 mb-4" />
              <h3 className="text-2xl font-medium text-slate-900 mb-3">Corporate Law</h3>
              <p className="text-slate-600 mb-4">
                From formation to complex M&A transactions, we provide strategic counsel for businesses at every stage.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Business Formation & Structuring</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Mergers & Acquisitions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Contract Drafting & Negotiation</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white border border-slate-100 p-8 shadow-sm flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <Shield className="h-12 w-12 text-amber-700 mb-4" />
              <h3 className="text-2xl font-medium text-slate-900 mb-3">Intellectual Property</h3>
              <p className="text-slate-600 mb-4">
                Protect your innovations and brand with comprehensive IP services from registration to enforcement.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Trademark & Copyright Registration</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>IP Portfolio Management</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Infringement Defense</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white border border-slate-100 p-8 shadow-sm flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <Users className="h-12 w-12 text-amber-700 mb-4" />
              <h3 className="text-2xl font-medium text-slate-900 mb-3">Regulatory Compliance</h3>
              <p className="text-slate-600 mb-4">
                Navigate complex regulations with confidence through proactive compliance strategies.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Anti-Corruption Compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Government Contracting</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <span>Regulatory Investigations</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              Why Leading Companies Choose Us
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              Trusted by businesses and individuals for exceptional legal representation
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-2 md:gap-12 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            <div className="space-y-8 flex-shrink-0 w-[85vw] md:w-auto snap-center">
              {[
                {
                  title: 'Proven Track Record',
                  description: 'Consistently delivering exceptional results and favorable outcomes for our clients across diverse legal matters.'
                },
                {
                  title: 'Industry Expertise',
                  description: 'Deep knowledge across technology, healthcare, finance, manufacturing, and professional services.'
                },
                {
                  title: 'Strategic Thinking',
                  description: 'We don\'t just solve legal problems—we help you achieve your business objectives.'
                },
                {
                  title: 'Responsive Service',
                  description: 'Available 24/7 for urgent matters with clear communication every step of the way.'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-amber-700 text-white flex items-center justify-center text-xl font-bold">
                      {idx + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden md:block flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <img
                src="https://images.unsplash.com/photo-1710472530204-63e0a09c309f?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Why choose us"
                className="w-full h-[400px] object-cover shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials - Hidden */}
      <section className="hidden py-24 md:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              Hear from those we've helped
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white border border-slate-100 shadow-sm p-8"
                data-testid={`testimonial-${idx}`}
              >
                <p className="text-slate-600 mb-6 italic leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg md:text-xl text-slate-600">
              Find answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="border border-slate-200 px-6" data-testid={`faq-item-${idx}`}>
                <AccordionTrigger className="text-left text-lg font-medium text-slate-900 hover:text-amber-700">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-slate-600 mb-6">Still have questions?</p>
            <button
              onClick={openLeadForm}
              className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-4 font-serif text-lg transition-all hover:tracking-wider shadow-lg hover:shadow-xl btn-primary"
              data-testid="faq-cta-button"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
